import React, { useState, useEffect } from 'react';

// material-ui
import { useTheme, styled } from '@mui/material/styles';
import {
  Avatar,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Typography
} from '@mui/material';

// assets
import { IconBuildingSkyscraper } from '@tabler/icons';

// styles
const ListItemWrapper = styled('div')(({ theme }) => ({
  cursor: 'pointer',
  padding: 16,
  '&:hover': {
    background: theme.palette.primary.light
  },
  '& .MuiListItem-root': {
    padding: 0
  }
}));

import { ThemeTypography } from 'themes/typography';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment-timezone';

import { GET_INCOMING_SHARES } from 'graphql/content-shares/content-shares.query';
import { REVOKE_CONTENT_SHARE, UPDATE_SHARE_STATUS } from 'graphql/content-shares/content-shares.mutation';

import { useToastAlert } from 'hooks/useToastAlertHook';

// ==============================|| NOTIFICATION LIST ITEM ||============================== //

const NotificationList = () => {
  const theme = useTheme<ThemeTypography>();
  const { handleToastAlert } = useToastAlert();

  const [incomingShares, setIncomingShares] = useState<
    { id: string; contentTitle: string; ownerCompanyName: string; status: string; createdAt: string }[]
  >([]);

  const chipSX = {
    height: 24,
    padding: '0 6px'
  };

  const chipErrorSX = {
    ...chipSX,
    color: theme.palette.orange.dark,
    backgroundColor: theme.palette.orange.light,
    marginRight: '5px'
  };

  const chipSuccessSX = {
    ...chipSX,
    color: theme.palette.success.dark,
    backgroundColor: theme.palette.success.light,
    height: 28
  };

  const [load, { data, refetch }] = useLazyQuery(GET_INCOMING_SHARES);

  const [revokeContentShare] = useMutation(REVOKE_CONTENT_SHARE, {
    onError: (error: ApolloError) => {
      handleToastAlert({
        open: true,
        message: error.message,
        severity: 'error'
      });
    }
  });
  const [updateShareStatus] = useMutation(UPDATE_SHARE_STATUS, {
    onError: (error: ApolloError) => {
      handleToastAlert({
        open: true,
        message: error.message,
        severity: 'error'
      });
    }
  });

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      load();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return () => (mounted = false as any);
  }, [load]);

  useEffect(() => {
    if (data) {
      setIncomingShares(data.getIncomingShares.result);
    }
  }, [data]);

  const handleRevokeAccess = (shareId: string) => {
    revokeContentShare({
      variables: { shareId },
      onCompleted: () => {
        refetch();
      }
    });
  };

  const handleUpdateAccess = (shareId: string) => {
    updateShareStatus({
      variables: { shareId, input: { status: 'accepted' } },
      onCompleted: () => {
        refetch();
      }
    });
  };

  if (incomingShares.length === 0) return <></>;

  return (
    <List
      sx={{
        width: '100%',
        maxWidth: 330,
        py: 0,
        borderRadius: '10px',
        [theme.breakpoints.down('md')]: {
          maxWidth: 300
        },
        '& .MuiListItemSecondaryAction-root': {
          top: 22
        },
        '& .MuiDivider-root': {
          my: 0
        },
        '& .list-container': {
          pl: 7
        }
      }}
    >
      {incomingShares.map((item) => (
        <>
          <ListItemWrapper key={item.id}>
            <ListItem alignItems="center">
              <ListItemAvatar>
                <Avatar
                  sx={{
                    color: theme.palette.grey.dark,
                    backgroundColor: theme.palette.grey.light,
                    border: 'none',
                    borderColor: theme.palette.grey.main
                  }}
                >
                  <IconBuildingSkyscraper stroke={1.5} size="1.3rem" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={<Typography variant="subtitle1">Você recebeu um convite</Typography>} />
              <ListItemSecondaryAction>
                <Grid container justifyContent="flex-end">
                  <Grid item xs={12}>
                    <Typography variant="caption" display="block" gutterBottom>
                      {moment.tz(item.createdAt, 'America/Sao_Paulo').fromNow()}
                    </Typography>
                  </Grid>
                </Grid>
              </ListItemSecondaryAction>
            </ListItem>
            <Grid container direction="column" className="list-container">
              <Grid item xs={12} sx={{ pb: 2 }}>
                <Typography variant="subtitle2">
                  O Conteúdo &quot;<strong>{item.contentTitle}</strong>&quot; está sendo compartilhado com a sua organização.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  {item.status === 'pending' && (
                    <Grid item>
                      <Chip onClick={() => handleRevokeAccess(item.id)} label="Rejeitar" sx={chipErrorSX} />
                      <Chip onClick={() => handleUpdateAccess(item.id)} label="Aceitar" sx={chipSuccessSX} />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </ListItemWrapper>

          {incomingShares.length - 1 !== incomingShares.indexOf(item) && <Divider />}
        </>
      ))}
    </List>
  );
};

export default NotificationList;
